import React, { useEffect, useState, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { M_StateActions } from "../M_Store/stateSlice";

import "./M_Table.css";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { DataTable } from "primereact/datatable/datatable.cjs.js";
import { Column } from "primereact/column/column.cjs.js";
import { ContextMenu } from "primereact/contextmenu/contextmenu.cjs.js";
import { Paginator } from "primereact/paginator/paginator.cjs.js";
import {
  CrudTable,
  BufferService,
  FiltersSortService,
  LoadingBar,
  LoadingContext,
  MButton,
  MMessageBoard,
  NavigateContext,
  MCheckBox,
} from "../../M_Components";
import PropTypes from "prop-types";
import { isFailurePayload, apiMessage } from "../../components/utils/apiResponse";
import { TableMessageBoards } from "./TableMessageBoards";
import {
  loadingStylesFunc,
  sortFields,
  crudInPopUp,
  columnOrderHeaderIcon,
  auditActionColor,
  sortAuditData,
  columnInfoHeaderIcon,
  getColumnObj,
  sessionStorageMachine,
  selectedIncomingValue,
  deRetardationString,
  getType,
  getValue,
  getColumnName,
  loadingStyle,
  getSeverity,
} from "./TableServices";
import { TableHeader } from "./TableHeader";

//#region Component

/**
 * Renders a generic table with filtering, sorting, selection, CRUD integration,
 * context menus, session persistence, and audit support.
 *
 * The component centralizes common table behavior used across multiple screens:
 * - metadata loading through OPTIONS;
 * - paged list retrieval through BufferService;
 * - global and advanced filtering;
 * - view persistence;
 * - context-menu actions;
 * - CRUD integration in popup or dedicated tab modes;
 * - normalized API failure handling through isFailurePayload/apiMessage.
 *
 * @param {object} props Component props.
 * @returns {JSX.Element} The rendered table component.
 */
export function MTable({
  entityName,
  useSessionStorage,
  crud,
  onPassLine,
  onPassContext,
  tableHeight,
  manualId,
  rolePermissions,
  lineContext,
  extraButtons,
  removeContextItems,
  onHideLabels,
  toUseFilter,
  toUseFilterName,
  selectSingle,
  refresh,
  setRefresh,
  isParentTable,
  isChildTable,
  setParentLine,
  parentLine,
  field,
  roleName,
  FILTER,
  replaceFunctions,
  color,
  secondColor,
  rowGroup,
  groupField,
  headerTemplate,
  footerTemplate,
  colorOptions,
  noSelect,
  maxRows,
  includeImportExcel,
  includeTemplateExcel,
  value,
  valueIdField,
  removeHeaders,
  filtersOnTable,
  infoElement,
  orderFields,
  hideDefaultColor,
  inputFilter,
  stopSaveRows,
  removeColumns,
  removeFilters,
  disable,
  tabsRailWidth,
  onDataLoaded,
  autoSelectFirstRow,
}) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  const ctx = useContext(LoadingContext);
  const ctx2 = useContext(NavigateContext);

  //#region Derived props and basic configuration

  /**
   * Resolves the primary table color, falling back to the shared context color.
   *
   * @returns {string} The color used as the primary accent.
   */
  function passColor() {
    return color || ctx.primaryColor || "var(--color-black)";
  }
  color = passColor();

  /**
   * Resolves the secondary table color, falling back to the shared context color.
   *
   * @returns {string} The color used for secondary text and accents.
   */
  function passSecondColor() {
    return secondColor || ctx.secondaryColor || "var(--color-black)";
  }
  secondColor = passSecondColor();

  removeHeaders = removeHeaders || [];

  /**
   * Resolves the name used to evaluate permissions for this table.
   *
   * @returns {string} The role-matching entity name.
   */
  function passNameForRole() {
    return roleName || entityName;
  }
  const nameForRole = passNameForRole();

  /**
   * Resolves whether the table uses single-selection mode.
   *
   * @returns {boolean} True when the table should select a single row.
   */
  function passSelectedSingle() {
    return selectSingle !== undefined ? selectSingle : false;
  }
  const isSelectSingle = passSelectedSingle();

  const passFilterName = toUseFilterName || "Default";

  /**
   * Resolves the initial saved-view map passed by the parent.
   *
   * @returns {Map<string, string>} The incoming saved-view map.
   */
  function passedFilter() {
    return toUseFilter || new Map();
  }
  const passFilter = passedFilter();

  const removeItem = removeContextItems || [];
  const newButtons = extraButtons || [];
  const roles = rolePermissions || [];
  const contextMenu = lineContext || [];
  const entity = entityName;

  /**
   * Resolves whether this table persists its state in session storage / redux state.
   *
   * @returns {boolean} True when page persistence is enabled.
   */
  function passIsPage() {
    return useSessionStorage !== undefined ? useSessionStorage : true;
  }
  const isPage = passIsPage();

  /**
   * Resolves whether CRUD behavior is enabled.
   *
   * @returns {boolean} True when CRUD behavior is enabled.
   */
  function passIsCrud() {
    return crud !== undefined ? crud : true;
  }
  const isCrud = passIsCrud();

  //#endregion

  //#region Responsive helpers and translation

  const adjustingTableMedia2 = useMediaQuery("(min-width:1200px)");
  const adjustingTableMedia3 = useMediaQuery("(min-width:500px)");
  const match = useMediaQuery("(max-width:920px)");
  const match2 = useMediaQuery("(max-width:820px)");
  const maxWidth = useMediaQuery("(min-width:1050px)");

  const { t, i18n } = useTranslation();
  const initialLanguage = useRef(i18n.language);

  //#endregion

  //#region State

  /**
   * Resolves the current table instance id from the selected tab when a manual id is not provided.
   */
  const [id, setId] = useState(
    manualId === undefined
      ? M_State.filter((obj) => obj.key === "selectedTab")[0]?.value
      : manualId
  );

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [firstRows, setFirstRows] = useState(0);
  const [page, setPage] = useState(1);

  /**
   * Resolves the initial page size.
   *
   * @returns {number} The default number of rows per page.
   */
  function passPageSize() {
    return maxRows || 20;
  }
  const [pageSize, setPageSize] = useState(passPageSize());
  const [totalRows, setTotalRows] = useState(50);

  const [selectedColumns, setSelectedColumns] = useState([]);

  /**
   * Resolves the initial selected rows from the incoming value prop.
   *
   * @returns {Array|object|string} The initial selected rows payload.
   */
  function passSelectedRows() {
    return (Array.isArray(value) && value && [...value]) || value || [];
  }
  const [selectedRows, setSelectedRows] = useState(passSelectedRows());

  const [selectContext, setSelectContext] = useState(null);

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [orderInfo, setOrderInfo] = useState({
    columnName: "",
    orderbool: false,
  });
  const [metaPair, setMetaPair] = useState([]);
  const [metaType, setMetaType] = useState([]);
  const [advFilterString, setAdvFilterString] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [expandedRows, setExpandedRows] = useState([]);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [valueAdd, setValueAdd] = useState(new Map());
  const [rowEdit, setRowEdit] = useState([]);
  const [editMap, setEditMap] = useState(new Map());
  const [row, setRow] = useState(null);
  const [operation, setOperation] = useState("");

  const [filteredColumnModal, setFilteredColumnModal] = useState([]);
  const [valueFilteredColumnModal, setValueFilteredColumnModal] = useState("");
  const [columnContextMenuString, setColumnContextMenuString] = useState({});

  const [views, setViews] = useState(passFilter);
  const [selectedView, setSelectedView] = useState(passFilterName);

  const [isFirstLoop, setIsFirstLoop] = useState(true);
  const [isTable, setIsTable] = useState(true);
  const [isLoaded, setIsLoaded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  //#endregion

  //#region Layout helpers

  /**
   * Computes the table scroll height from the viewport and known layout blocks.
   *
   * @returns {number|string} The computed table height.
   */
  const computeTableHeight = React.useCallback(() => {
    if (tableHeight) return tableHeight;

    let viewportHeight = 800;
    if (typeof window !== "undefined" && window.innerHeight) {
      viewportHeight = window.innerHeight;
    } else if (document.documentElement?.clientHeight) {
      viewportHeight = document.documentElement.clientHeight;
    }

    const headerSize =
      document.body.getElementsByClassName("p-datatable-header")[0]
        ?.offsetHeight || 60;
    const paginatorSize =
      document.body.getElementsByClassName("paginator")[0]?.offsetHeight || 60;
    const tabSize =
      document.body.getElementsByClassName("divTabs")[0]?.offsetHeight || 50;

    const available =
      viewportHeight - (headerSize + paginatorSize + tabSize + 3);

    return available > 200 ? available : 200;
  }, [tableHeight]);

  const [height, setHeight] = useState(() => computeTableHeight());

  /**
   * Keeps the table height aligned with viewport changes.
   */
  useEffect(() => {
    const handleResize = () => {
      setHeight(computeTableHeight());
    };

    handleResize();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }

    return undefined;
  }, [computeTableHeight]);

  const tabsExtraStr =
    typeof tabsRailWidth === "number"
      ? `${tabsRailWidth || 0}px`
      : tabsRailWidth || "0px";

  /**
   * Expands the table minimum width when the tabs rail occupies horizontal space.
   */
  const tableMinWidth = React.useMemo(() => {
    if (!tabsRailWidth || tabsExtraStr === "0px") return "100%";
    return `calc(100% + ${tabsExtraStr})`;
  }, [tabsRailWidth, tabsExtraStr]);

  //#endregion

  //#region Services and refs

  const bufferService = new BufferService(entity);
  const filterService = new FiltersSortService();
  const auditService = new BufferService("audit");

  const dt = useRef(null);
  const cm = useRef(null);
  const ccm = useRef(null);

  const [openAudit, setOpenAudit] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const [dataContextMenu, setDataContextMenu] = useState(null);

  //#endregion

  //#region Shared API helpers

  /**
   * Shows a normalized API error toast.
   *
   * @param {any} payload API payload or thrown error.
   * @param {string} fallback Translation key or fallback text.
   */
  const showApiError = (payload, fallback) => {
    ctx.toastMessages("error", "", apiMessage(payload, fallback));
  };

  /**
   * Starts a table-scoped loading cycle.
   *
   * Table data and metadata refreshes are rendered inside the table area so they
   * do not activate the application-level loader.
   */
  const startTableLoad = () => {
    setLoading(true);
  };

  /**
   * Ends a table-loading cycle and notifies the parent when required.
   */
  const finalizeTableLoad = () => {
    setLoading(false);
    setIsLoaded(false);

    if (typeof onDataLoaded === "function") {
      onDataLoaded();
    }
  };

  /**
   * Clears the visible table data after a failed request.
   */
  const clearTablePayload = () => {
    setRows([]);
    setTotalRows(0);

    if (groupField) {
      setExpandedRows([]);
    }
  };

  /**
   * Applies a successful paged payload to the table state.
   *
   * @param {object} payload Paged API payload.
   */
  const applyTablePayload = (payload) => {
    selectedIncomingValue(
      payload,
      value,
      valueIdField,
      selectedRows,
      setSelectedRows
    );

    setTotalRows(payload?.TotalItems ?? 0);
    setRows(payload?.Items ?? []);

    if (groupField) {
      setExpandedRows(payload?.Items ?? []);
    }
  };

  /**
   * Executes a table list request and applies the result through the normalized API helpers.
   *
   * @param {Promise<any>} requestPromise Promise returned by BufferService.
   * @param {string} fallbackMessage Fallback text used when the API does not provide a message.
   */
  const runTableRequest = async (
    requestPromise,
    fallbackMessage = t("ErrorLoadingData")
  ) => {
    try {
      const res = await requestPromise;

      finalizeTableLoad();

      if (isFailurePayload(res)) {
        showApiError(res, fallbackMessage);
        clearTablePayload();
        return;
      }

      applyTablePayload(res);
    } catch (err) {
      finalizeTableLoad();
      showApiError(err, fallbackMessage);
      clearTablePayload();
    }
  };

  /**
   * Builds the default saved-view map for the current page size.
   *
   * @returns {Map<string, string>} Default view map.
   */
  const buildDefaultViewMap = () => {
    return new Map([["Default", `?page=1&pagesize=${passPageSize()}`]]);
  };

  //#endregion

  //#region Context menu metadata

  /**
   * Builds the column-level context menu used for sorting and column visibility.
   */
  const columnContextMenu = [
    {
      label: t("OrderAsc"),
      icon: (
        <i className="pi pi-sort-amount-down-alt" style={{ color: color }}></i>
      ),
      command: () => {
        const info = {
          columnName: columnContextMenuString.target.title,
          orderbool: false,
        };
        setOrderInfo(info);
        getWithFilters(info);
      },
    },
    {
      label: t("OrderDesc"),
      icon: (
        <i className="pi pi-sort-amount-up-alt" style={{ color: color }}></i>
      ),
      command: () => {
        const info = {
          columnName: columnContextMenuString.target.title,
          orderbool: true,
        };
        setOrderInfo(info);
        getWithFilters(info);
      },
    },
    {
      label: t("Remove"),
      icon: <i className="pi pi-times" style={{ color: color }}></i>,
      command: () =>
        onColumnToggle(getColumnObj(columnContextMenuString.target.title)),
    },
  ];

  //#endregion

  //#region Persistence metadata

  const sessionKeys = [
    "rows",
    "columns",
    "selectedColumns",
    "globalFilterValue",
    "metaPair",
    "advFilterString",
    "filteredColumnModal",
    "valueFilteredColumnModal",
    "selectedRows",
    "metaType",
    "showFilters",
    "orderInfo",
    "operation",
    "isTable",
    "rowEdit",
    "valueAdd",
    "editMap",
    "row",
    "totalRows",
    "views",
    "selectedView",
    "pageSize",
    "page",
    "firstRows",
    "companies",
    "expandedRows",
    "language",
  ];

  /**
   * Captures the current state snapshot in the same order used by the persistence keys.
   *
   * @returns {Array<any>} Current state snapshot.
   */
  function getSessionKeys() {
    return [
      rows,
      columns,
      selectedColumns,
      globalFilterValue,
      metaPair,
      advFilterString,
      filteredColumnModal,
      valueFilteredColumnModal,
      selectedRows,
      metaType,
      showFilters,
      orderInfo,
      operation,
      isTable,
      rowEdit,
      valueAdd,
      editMap,
      row,
      totalRows,
      views,
      selectedView,
      pageSize,
      page,
      firstRows,
      companies,
      expandedRows,
      i18n.language,
    ];
  }

  const dataMappings = {
    rows: setRows,
    columns: setColumns,
    selectedColumns: setSelectedColumns,
    globalFilterValue: setGlobalFilterValue,
    metaPair: setMetaPair,
    advFilterString: setAdvFilterString,
    filteredColumnModal: setFilteredColumnModal,
    valueFilteredColumnModal: setValueFilteredColumnModal,
    metaType: setMetaType,
    showFilters: setShowFilters,
    orderInfo: setOrderInfo,
    operation: setOperation,
    isTable: setIsTable,
    rowEdit: setRowEdit,
    valueAdd: (valueToSet) => setValueAdd(new Map(valueToSet)),
    editMap: (valueToSet) => setEditMap(new Map(valueToSet)),
    row: setRow,
    totalRows: setTotalRows,
    views: (valueToSet) => setViews(new Map(valueToSet)),
    selectedView: setSelectedView,
    pageSize: setPageSize,
    page: setPage,
    firstRows: setFirstRows,
    companies: setCompanies,
    expandedRows: setExpandedRows,
  };

  //#endregion

  //#region Effects

  /**
   * Performs the first table bootstrap for the current instance id.
   */
  useEffect(() => {
    if (!isFirstLoop) return;

    setIsLoaded(true);
    getSessionStorageData(id);
    getPermissions();
    setIsFirstLoop(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isFirstLoop]);

  /**
   * Rebuilds metadata and visible columns when the language changes.
   */
  useEffect(() => {
    if (i18n.language === initialLanguage.current) return;

    setMetaType([]);
    setColumns([]);
    setSelectedColumns([]);
    setFilteredColumnModal([]);
    setAllColumns([]);
    setViews(new Map());
    setSelectedView("Default");
    options();
    setRefresh?.((r) => !r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /**
   * Applies an external refresh request by reloading the current view.
   */
  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      changeView(selectedView);
      setLoading(true);
    }
  }, [refresh, selectedView, setRefresh]);

  /**
   * Notifies the parent when the layout width suggests hiding labels.
   */
  useEffect(() => {
    if (onHideLabels) onHideLabels(maxWidth);
  }, [onHideLabels, maxWidth]);

  /**
   * Reacts to tab or manual-id changes and restores the corresponding table snapshot.
   */
  useEffect(() => {
    if (manualId === undefined) {
      const newId = M_State.filter((obj) => obj.key === "selectedTab")[0]
        ?.value;

      if (newId !== id) {
        setId(newId);
        getSessionStorageData(newId);
      }

      return;
    }

    if (manualId !== id) {
      setId(manualId);
      getSessionStorageData(manualId);
    }
  }, [manualId, id, M_State]);

  /**
   * Persists the current table state to the shared session state when enabled.
   */
  useEffect(() => {
    if (isPage) {
      const tempArray = [];

      sessionKeys.forEach((key, index) => {
        const valueToPersist = sessionStorageMachine(
          id,
          key,
          getSessionKeys()[index],
          stopSaveRows
        );

        if (valueToPersist) tempArray.push(valueToPersist);
      });

      dispatch(M_StateActions.addM_State({ key: id, value: tempArray }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    rows,
    columns,
    selectedColumns,
    globalFilterValue,
    metaPair,
    advFilterString,
    filteredColumnModal,
    valueFilteredColumnModal,
    selectedRows,
    metaType,
    showFilters,
    orderInfo,
    operation,
    isTable,
    rowEdit,
    valueAdd,
    editMap,
    row,
    totalRows,
    views,
    selectedView,
    pageSize,
    page,
    firstRows,
    companies,
    expandedRows,
  ]);

  /**
   * Reloads the current view when the incoming external filter changes.
   */
  useEffect(() => {
    if (isLoaded) return;
    changeView(selectedView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FILTER]);

  /**
   * Automatically selects the first row when the table is in single-selection mode
   * and the caller explicitly requests automatic first-row selection.
   */
  useEffect(() => {
    if (!autoSelectFirstRow) return;
    if (noSelect) return;
    if (!isSelectSingle) return;
    if (!rows || rows.length === 0) return;
    if (selectedRows && selectedRows.length > 0) return;

    const firstRow = rows[0];
    const newSelection = [firstRow];

    setSelectedRows(newSelection);

    if (isParentTable && setParentLine) {
      setParentLine(newSelection);
    }

    if (onPassLine) {
      onPassLine(newSelection);
    }
  }, [
    autoSelectFirstRow,
    noSelect,
    isSelectSingle,
    rows,
    selectedRows,
    isParentTable,
    setParentLine,
    onPassLine,
  ]);

  //#endregion

  //#region Session and metadata loading

  /**
   * Restores session data for the given table instance id or falls back to metadata loading.
   *
   * @param {string|number} sessionID Table instance id.
   */
  const getSessionStorageData = (sessionID) => {
    setIsLoaded(true);

    if (!isPage) {
      options();
      setViews(buildDefaultViewMap());
      return;
    }

    const M_StateData = M_State.find((obj) => obj.key === sessionID)?.value;
    const extractValue = (key, defaultValue = null) =>
      M_StateData?.find((obj) => obj.key === key)?.value ?? defaultValue;

    if (!M_StateData) {
      options();
      return;
    }

    Object.entries(dataMappings).forEach(([key, setter]) => {
      const valueToRestore = extractValue(key);
      if (valueToRestore !== undefined) setter(valueToRestore);
    });

    const tempColumns = extractValue("columns");

    if (
      tempColumns &&
      sessionKeys.some((key) => extractValue(key) !== undefined)
    ) {
      setIsLoaded(false);

      if (stopSaveRows) {
        getWithFilters(
          extractValue("orderInfo"),
          extractValue("page"),
          extractValue("pageSize"),
          extractValue("metaPair")
        );
      }

      return;
    }

    options();
    setViews(buildDefaultViewMap());
  };

  /**
   * Loads the table metadata through OPTIONS and prepares columns, filters, and view information.
   */
  const options = async () => {
    setMetaType([]);
    setColumns([]);
    setSelectedColumns([]);
    setFilteredColumnModal([]);

    startTableLoad();

    try {
      const data = await bufferService.options();

      if (isFailurePayload(data)) {
        finalizeTableLoad();
        showApiError(data, t("ErrorLoadingData"));
        return;
      }

      const tempArray = [];
      const tempColumnArray = [];
      const tempSelectedColumnsArray = [];
      const tempFilteredColumnArray = [];
      const tempMetaType = [];

      let tempCompanies = M_State.filter((obj) => obj.key === "companies")[0]
        ?.value;
      if (!tempCompanies?.[0]?.AccountId) {
        tempCompanies = [];
      }
      setCompanies(tempCompanies);

      const orderedData = (data || []).slice().sort((a, b) => {
        const aOrder = typeof a.Order === "number" ? a.Order : 0;
        const bOrder = typeof b.Order === "number" ? b.Order : 0;
        return aOrder - bOrder;
      });

      orderedData.forEach((element) => {
        const isFKText = element.IsForeignKey && element.GroupByInfo;

        tempMetaType.push({
          metaName: element.Name,
          metaType: isFKText ? "string" : element.Type,
          metaDisplay: element.NormalizedName,
          metaIsList: element.IsHiddenList,
          metaIsEdit: element.IsHiddenEdit,
          metaMinLen: element.MinLen,
          metaMaxLen: element.MaxLen,
          metaIsRequired: element.IsRequired,
          metaIsForeignKey: element.IsForeignKey,
          metaForeignKeyName: element.ForeignKeyName,
          metaForeignTableName: element.ForeignTableName,
          metaForeignTableToPassId: element.ForeignTableToPassId,
          metaGroupByInfo: element.GroupByInfo,
        });

        if (element.IsHiddenList) return;

        tempArray.push({ ...element, name: element.Name });
        tempColumnArray.push({ ...element, name: element.Name });
        tempSelectedColumnsArray.push({ ...element, name: element.Name });
        tempFilteredColumnArray.push({ ...element, name: element.Name });
      });

      setMetaType(tempMetaType);
      setFilteredColumnModal(tempFilteredColumnArray);
      setSelectedColumns(tempSelectedColumnsArray);
      setColumns(tempColumnArray);
      setAllColumns(tempArray);

      changeView(selectedView);
    } catch (err) {
      finalizeTableLoad();
      showApiError(err, t("ErrorLoadingData"));
    }
  };

  /**
   * Resolves the current permissions from the provided role-permission tokens.
   */
  const getPermissions = () => {
    roles.forEach((role) => {
      if (role.match(nameForRole)) {
        const tempPerm = role.split(nameForRole)[1];
        setPermissions((prev) => [...prev, tempPerm]);
      }
    });
  };

  //#endregion

  //#region Data loading

  /**
   * Loads the default table data without a saved-view override.
   */
  const get = async () => {
    setRows([]);

    startTableLoad();

    const newUrl = filterService.queryBuilder(
      page,
      pageSize,
      undefined,
      undefined,
      FILTER
    );

    await runTableRequest(
      bufferService.filter(newUrl),
      t("ErrorLoadingData")
    );
  };

  /**
   * Applies the selected saved view by translating its URL parts into filter and sort state,
   * then reloading the table data.
   *
   * @param {string} viewName Saved-view name.
   */
  const changeView = async (viewName) => {
    startTableLoad();

    setAdvFilterString("");
    setMetaPair([]);
    setOrderInfo({ columnName: "", orderbool: false });
    setSelectedView(viewName);

    const url = views.get(viewName);
    let objectFilterArray = [];
    let objectOrderBy = { columnName: "", orderbool: false };

    if (url === undefined) {
      await get();
      return;
    }

    url.split("&").forEach((elem) => {
      if (elem.includes("filter")) {
        setAdvFilterString("&" + elem);

        let stringFilter = elem.split("filter=")[1];
        stringFilter = stringFilter.split(" and ");

        stringFilter.forEach((el) => {
          const tempArray = el.split(" ");
          let tempObj;
          let tempOp = "";

          if (tempArray[0] === "") tempArray.shift();

          tempObj = getColumnName(tempArray, metaType);

          if (tempArray[1] === "eq") {
            if (tempArray[1] === "eq" && tempArray[2].includes("$")) {
              tempOp = "Contains";
            } else {
              tempOp = "Equals";
            }
          }

          if (tempArray[1] === "Greater") tempOp = "Greater";
          if (tempArray[1] === "Lesser") tempOp = "Lesser";

          let objectFilter;
          if (tempObj.length) {
            objectFilter = {
              name: tempArray[0],
              display: tempObj[0].metaDisplay,
              type: tempObj[0].metaType,
              operation: tempOp,
              value: tempArray[2].replaceAll("'", "").replaceAll("$", ""),
            };
          } else if (tempArray[0].includes("contains")) {
            tempOp = "Contains";
            objectFilter = {
              name: tempArray[0].split(",")[0].replace("contains(", ""),
              display: deRetardationString(
                tempArray[0].split(",")[0].replace("contains(", "")
              ),
              type: "string",
              operation: tempOp,
              value: decodeURIComponent(tempArray[0])
                .split(",")[1]
                .replace(")", "")
                .replaceAll("'", ""),
            };
          } else {
            objectFilter = {
              name: tempArray[0],
              display: deRetardationString(tempArray[0]),
              type: getType(
                tempArray[2].replaceAll("'", "").replaceAll("$", "")
              ),
              operation: tempOp,
              value: getValue(
                tempArray[2].replaceAll("'", "").replaceAll("$", "")
              ),
            };
          }

          objectFilterArray.push(objectFilter);
        });
      }

      if (elem.includes("order")) {
        let stringOrder = elem.split("orderby=")[1];
        stringOrder = stringOrder.split(" ");

        let tempBool = "";
        if (stringOrder[1] === "asc") tempBool = false;
        if (stringOrder[1] === "desc") tempBool = true;

        objectOrderBy = {
          columnName: stringOrder[0].toUpperCase(),
          orderbool: tempBool,
        };
      }
    });

    setMetaPair(objectFilterArray);
    setOrderInfo(objectOrderBy);

    const newUrl = filterService.queryBuilder(
      page,
      pageSize,
      objectOrderBy,
      objectFilterArray,
      FILTER
    );

    await runTableRequest(
      bufferService.filter(newUrl),
      t("ErrorLoadingData")
    );
  };

  /**
   * Loads data using the provided paging, ordering, and advanced-filter state.
   *
   * @param {object} ord Ordering descriptor.
   * @param {number} newPage Page number override.
   * @param {number} newRows Page size override.
   * @param {Array<object>} adv Advanced-filter descriptor list.
   */
  const getWithFilters = async (ord, newPage, newRows, adv) => {
    startTableLoad();

    const customPage = newPage || page;
    const customPageSize = newRows || pageSize;
    const customOrder = ord || orderInfo;
    const customAdv = adv || metaPair;

    const url = filterService.queryBuilder(
      customPage,
      customPageSize,
      customOrder,
      customAdv,
      FILTER
    );

    await runTableRequest(
      bufferService.filter(url),
      t("ErrorLoadingData")
    );
  };

  //#endregion

  //#region Sorting, filtering, and paging

  /**
   * Applies sorting when a sortable header is clicked.
   *
   * @param {MouseEvent} e Header click event.
   */
  const order = async (e) => {
    if (e.target.id !== "columnTable") return;

    setLoading(true);

    const column = e.target.title;
    let newOrderInfo = {};

    if (orderInfo.columnName === column) {
      if (orderInfo.orderbool === false) {
        const resetOrder = { columnName: "", orderbool: false };
        setOrderInfo(resetOrder);

        if (globalFilterValue !== "") {
          globalSearch(globalFilterValue, resetOrder);
        } else {
          getWithFilters(resetOrder);
        }

        return;
      }

      newOrderInfo = { columnName: column, orderbool: !orderInfo.orderbool };
    } else {
      newOrderInfo = { columnName: column, orderbool: true };
    }

    setOrderInfo(newOrderInfo);

    if (globalFilterValue !== "") {
      globalSearch(globalFilterValue, newOrderInfo);
      return;
    }

    const newUrl = filterService.queryBuilder(
      page,
      pageSize,
      newOrderInfo,
      metaPair,
      FILTER
    );

    await runTableRequest(
      bufferService.filter(newUrl),
      t("ErrorLoadingData")
    );
  };

  /**
   * Toggles a column in the current visible-column list.
   *
   * @param {object} data Column metadata.
   */
  const onColumnToggle = (data) => {
    let tempArray = [];

    if (selectedColumns.find((element) => element.Name === data.name)) {
      tempArray = selectedColumns.filter((el) => el.Name !== data.name);
      setSelectedColumns(tempArray);
    } else {
      setSelectedColumns((prev) => [...prev, data]);
    }
  };

  /**
   * Opens the delete confirmation dialog for a single row or for the current selection.
   *
   * @param {object|Array<object>} line Row payload or selected row collection.
   */
  const handleOpenDelete = (line) => {
    if (Array.isArray(line) && line.length > 0) {
      setDataContextMenu(null);
      setOpenConfirmDelete(true);
    } else if (
      line &&
      typeof line === "object" &&
      Object.keys(line).length > 0
    ) {
      setDataContextMenu(line);
      setOpenConfirmDelete(true);
    } else {
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  /**
   * Closes the delete confirmation dialog and resets the context row.
   */
  const handleCloseConfirmDelete = () => {
    setDataContextMenu(null);
    setOpenConfirmDelete(false);
  };

  /**
   * Updates the current page and page size from the paginator and reloads the data.
   *
   * @param {object} event PrimeReact paginator event.
   */
  const onPage = (event) => {
    setFirstRows(event.first);
    setExpandedRows([]);

    const newPage = event.page + 1;
    setPage(newPage);
    setPageSize(event.rows);

    getWithFilters("", newPage, event.rows);
  };

  /**
   * Opens the column context menu only when the user right-clicks on a table header.
   *
   * @param {MouseEvent} e Context-menu event.
   */
  const rightClick = (e) => {
    if (e.target.id !== "columnTable") return;
    setColumnContextMenuString(e);
    ccm.current.show(e);
  };

  /**
   * Executes a global search across string and integer columns.
   *
   * @param {string} valueSearch Search text.
   * @param {object} orderOverride Optional order override.
   */
  const globalSearch = async (valueSearch, orderOverride) => {
    startTableLoad();

    const tempNewValue = valueSearch;
    let tempString = "";
    let first = true;
    const tempOrder = orderOverride || orderInfo;
    let tempFullQuery = "";

    setGlobalFilterValue(tempNewValue);

    if (tempNewValue !== "") {
      metaType.forEach((tempCol) => {
        switch (tempCol.metaType) {
          case "string":
            tempString = tempString.concat(
              first
                ? ` contains(${tempCol.metaName}, '${encodeURIComponent(
                  tempNewValue
                )}')`
                : ` or contains(${tempCol.metaName}, '${encodeURIComponent(
                  tempNewValue
                )}')`
            );
            first = false;
            break;

          case "int32":
          case "int":
            if (Number(tempNewValue) || Number(tempNewValue) === 0) {
              tempString = tempString.concat(
                first
                  ? ` ${tempCol.metaName} eq ${encodeURIComponent(
                    tempNewValue
                  )}`
                  : ` or ${tempCol.metaName} eq ${encodeURIComponent(
                    tempNewValue
                  )}`
              );
              first = false;
            }
            break;

          default:
            break;
        }
      });

      tempFullQuery = FILTER
        ? filterService.queryBuilder(
          page,
          pageSize,
          tempOrder,
          metaPair,
          FILTER + " and (" + tempString + ")"
        )
        : filterService.queryBuilder(
          page,
          pageSize,
          tempOrder,
          metaPair,
          tempString
        );
    } else {
      tempFullQuery = filterService.queryBuilder(
        page,
        pageSize,
        tempOrder,
        metaPair,
        FILTER
      );
    }

    await runTableRequest(
      bufferService.filter(tempFullQuery),
      t("ErrorLoadingData")
    );
  };

  //#endregion

  //#region CRUD actions and selection

  /**
   * Opens the copy flow for the selected row.
   *
   * @param {object|null} selected Selected row.
   */
  const handleOpenCopy = (selected) => {
    const rowToCopy = selected || null;

    if (rowToCopy) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("Copy", rowToCopy);
        return;
      }

      setRow(rowToCopy);
      setOperation("Copy");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  /**
   * Opens the read-only view flow for the selected row.
   *
   * @param {object|null} selected Selected row.
   */
  function handleView(selected) {
    const rowToView = selected || null;

    if (rowToView) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("View", rowToView);
        return;
      }

      setRow(rowToView);
      setOperation("View");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  }

  /**
   * Opens the create flow.
   */
  const handleOpenDialogAdd = () => {
    if (!crudInPopUp(metaType)) {
      sendInfoCrud("Create");
      return;
    }

    setOperation("Create");
    setRow(null);
    setIsTable(false);
  };

  /**
   * Opens the edit flow for the selected row.
   *
   * @param {object|null} selected Selected row.
   */
  const handleOpenEdit = (selected) => {
    const rowToEdit = selected || null;

    if (rowToEdit) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("Edit", rowToEdit);
        return;
      }

      setRow(rowToEdit);
      setOperation("Edit");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  /**
   * Applies DataTable selection changes for single-selection, multi-selection,
   * and select-all scenarios.
   *
   * @param {object} e PrimeReact selection event.
   */
  const onSelectionChange = (e) => {
    let tempValue = [];

    if (isSelectSingle) {
      if (e.value) tempValue = [e.value];
      else tempValue = [];
    } else if (e.type === "all") {
      if (e.value.length) {
        (Array.isArray(selectedRows) ? selectedRows : []).forEach((obj) => {
          if (!e.value.find((rowObj) => rowObj.Id === obj.Id)) {
            tempValue.push(obj);
          }
        });

        tempValue.push(...e.value);
      } else {
        (Array.isArray(selectedRows) ? selectedRows : []).forEach((obj) => {
          if (!rows.find((rowObj) => rowObj.Id === obj.Id)) {
            tempValue.push(obj);
          }
        });
      }
    } else {
      tempValue = [...e.value];
    }

    if (isParentTable && setParentLine) {
      setParentLine(tempValue);
    }

    if (onPassLine) {
      onPassLine(tempValue);
    }

    setSelectedRows([...tempValue]);
  };

  /**
   * Executes the delete workflow for the context row or for all selected rows.
   *
   * The workflow uses the normalized API payload contract exposed by BufferService,
   * so success is only shown when every delete request succeeds.
   */
  const postDelete = async () => {
    const rowsToDelete = dataContextMenu
      ? [dataContextMenu]
      : Array.isArray(selectedRows)
        ? selectedRows
        : [];

    if (!rowsToDelete.length) {
      ctx.toastMessages("error", "", t("SelectedLine"));
      return;
    }

    ctx.setIsLoad(true);

    try {
      const results = await Promise.all(
        rowsToDelete.map(async (elem) => {
          const response = await bufferService.delete(elem.Id);

          return {
            id: elem.Id,
            response,
          };
        })
      );

      const failed = results.filter((x) => isFailurePayload(x.response));

      if (failed.length === results.length) {
        showApiError(failed[0]?.response, t("LabelTryAgain"));
        return;
      }

      if (failed.length > 0) {
        showApiError(failed[0]?.response, t("LabelTryAgain"));
      } else {
        ctx.toastMessages("success", "", t("RemoveLine"));
      }

      setDataContextMenu(null);
      setOpenConfirmDelete(false);
      setSelectedRows([]);
      getWithFilters();
    } catch (err) {
      showApiError(err, t("LabelTryAgain"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /**
   * Loads audit history for the selected row.
   *
   * @param {object|null} line Selected row.
   */
  const getAudit = async (line) => {
    if (!line) {
      ctx.toastMessages("error", "", t("SelectedLine"));
      return;
    }

    ctx.setIsLoad(true);

    try {
      const resAudit = await auditService.getId(line?.Id);

      if (isFailurePayload(resAudit)) {
        showApiError(resAudit, t("noAudit"));
        return;
      }

      if (Array.isArray(resAudit)) {
        setAuditData(resAudit);
        setOpenAudit(true);
        return;
      }

      ctx.toastMessages("error", "", t("noAudit"));
    } catch (err) {
      showApiError(err, t("noAudit"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /**
   * Closes the audit dialog and clears the loaded audit data.
   */
  const closeAudit = () => {
    setOpenAudit(false);
    setAuditData([]);
  };

  /**
   * Prepares the delete flow from the current context-menu selection.
   */
  function contextDeleteButton() {
    const tempObj = { ...selectContext };
    setDataContextMenu(tempObj);
    handleOpenDelete(selectContext);
  }

  /**
   * Rebuilds the row context menu from permissions, configuration, and extra actions.
   *
   * @returns {Array<object>} PrimeReact context-menu model.
   */
  const rebuildContextMenu = () => {
    const tempArray = [];

    if (!removeItem.includes("View")) {
      tempArray.push({
        label: t("view"),
        icon: (
          <i
            className="pi pi-search"
            style={{ color: color, paddingRight: "5px" }}
          ></i>
        ),
        command: () => rebuildContextCommand("View"),
      });
    }

    if (permissions.includes("Update") && !removeItem.includes("Update")) {
      tempArray.push({
        label: t("Edit"),
        icon: "pi pi-pencil",
        command: () => rebuildContextCommand("Edit"),
      });
    }

    if (
      permissions.includes("Create") &&
      !removeItem.includes("Create") &&
      !removeItem.includes("Copy")
    ) {
      tempArray.push({
        label: t("Copy"),
        icon: "pi pi-copy",
        command: () => rebuildContextCommand("Copy"),
      });
    }

    if (permissions.includes("Delete") && !removeItem.includes("Delete")) {
      tempArray.push({
        label: t("Delete"),
        icon: "pi pi-trash",
        command: () => rebuildContextCommand("Delete"),
      });
    }

    if (permissions.includes("Audit") && !removeItem.includes("Audit")) {
      tempArray.push({
        label: t("Audit"),
        icon: "pi pi-search",
        command: () => getAudit(selectContext),
      });
    }

    if (contextMenu.length) {
      contextMenu.forEach((objContext) => {
        tempArray.push({
          label: objContext.label,
          icon: objContext.icon,
          command: () => objContext.command(selectContext),
        });
      });
    }

    return tempArray;
  };

  /**
   * Resolves a row context-menu command either from replacement handlers or from the default behavior.
   *
   * @param {string} operationCmd Requested operation key.
   * @returns {any} Command result.
   */
  function rebuildContextCommand(operationCmd) {
    if (replaceFunctions?.get(operationCmd)) {
      return replaceFunctions?.get(operationCmd)(selectContext);
    }

    switch (operationCmd) {
      case "View":
        return handleView(selectContext);
      case "Edit":
        return handleOpenEdit(selectContext);
      case "Copy":
        return handleOpenCopy(selectContext);
      case "Delete":
        return contextDeleteButton();
      default:
        return undefined;
    }
  }

  /**
   * Opens the external CRUD page in a dedicated tab.
   *
   * @param {string} tempOperation CRUD operation.
   * @param {object|null} line Selected row.
   */
  function sendInfoCrud(tempOperation, line) {
    ctx.setPassValue({
      metaType: metaType,
      entity: entity,
      operation: tempOperation,
      row: line ?? null,
      isChildTable: isChildTable ?? null,
      parentLine: parentLine ?? null,
      field: field ?? null,
      companies: companies ?? null,
    });

    ctx2.newTab("createPage", tempOperation + " " + entity);
  }

  //#endregion

  //#region Cell renderers and header helpers

  /**
   * Renders a boolean cell as a disabled checkbox.
   *
   * @param {object} rowData Row data.
   * @param {string} fieldName Field name.
   * @returns {JSX.Element} Checkbox cell.
   */
  const verifiedBodyTemplateLocal = (rowData, fieldName) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <MCheckBox disabled checked={rowData[fieldName]} noPadding />
      </div>
    );
  };

  /**
   * Renders a status-like field with severity coloring.
   *
   * @param {object} rowData Row data.
   * @param {string} fieldName Field name.
   * @returns {JSX.Element} Colored status cell.
   */
  const statusBodyTemplateLocal = (rowData, fieldName) => {
    const colorBody = getSeverity(rowData[fieldName], fieldName);

    if (hideDefaultColor && colorBody === "#000000") {
      return (
        <div
          style={{
            display: "flex",
            fontWeight: "bolder",
            justifyContent: "center",
            textAlign: "center",
            alignItems: "center",
            borderRadius: "6px",
            padding: "3px 10px",
            fontSize: "0.8rem",
          }}
        >
          {t(rowData[fieldName])}
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          fontWeight: "bolder",
          color: "white",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          borderRadius: "6px",
          padding: "3px 10px",
          fontSize: "0.8rem",
          backgroundColor: colorBody,
        }}
      >
        {t(rowData[fieldName])}
      </div>
    );
  };

  /**
   * Renders a byte-array field as an image preview.
   *
   * @param {object} rowData Row data.
   * @param {string} fieldName Field name.
   * @returns {JSX.Element} Image cell.
   */
  const imageBodyTemplateLocal = (rowData, fieldName) => {
    return (
      <div
        style={{
          width: "50px",
          display: "flex",
          fontWeight: "bolder",
          color: "white",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          borderRadius: "6px",
          padding: "2px 10px",
          fontSize: "0.8rem",
        }}
      >
        <img
          width={"25px"}
          src={
            rowData[fieldName]
              ? `data:image/jpeg;base64,${rowData[fieldName]}`
              : "./images/noImage.png"
          }
          alt={fieldName}
        />
      </div>
    );
  };

  /**
   * Renders a datetime value with a simplified visual format.
   *
   * @param {object} rowData Row data.
   * @param {string} fieldName Field name.
   * @returns {JSX.Element|null} Datetime cell.
   */
  const dateTimeBodyTemplateLocal = (rowData, fieldName) => {
    const raw = rowData[fieldName];
    if (!raw) return null;

    const text = raw.toString().replace("T", " ").split(".")[0];

    return <span>{text}</span>;
  };

  /**
   * Renders the header cell with sorting icon and optional info tooltip.
   *
   * @param {object} col Column metadata.
   * @param {string} iconForm Sort icon mode.
   * @returns {JSX.Element|undefined} Header element.
   */
  const headerElement = (col, iconForm) => {
    if (
      col.Type !== "boolean" &&
      col.Type !== "string" &&
      col.Type !== "int32" &&
      col.Type !== "datetime" &&
      col.Type !== "int" &&
      col.Type !== "byte[]" &&
      col.Type !== "bool"
    ) {
      if (!removeHeaders.includes(col.Header)) {
        return (
          <div
            id="columnTable"
            title={col.Header}
            style={{ display: "flex", cursor: "pointer" }}
          >
            <div
              id="columnTable"
              title={col.Header}
              style={{ marginRight: "10px" }}
            >
              {col.NormalizedName}
            </div>

            {infoElement?.get(col.Header) && (
              <div className="tooltip">
                <span className="tooltiptext">
                  <ul style={{ listStyle: "none" }}>
                    {infoElement.get(col.Header).map((info, index) => (
                      <li
                        key={info + index || index}
                        style={{ marginBottom: "10px" }}
                      >
                        {info}
                      </li>
                    ))}
                  </ul>
                </span>
              </div>
            )}
          </div>
        );
      }
    }

    if (!removeHeaders.includes(col.Header)) {
      return (
        <MButton
          bold
          noHover
          marginNone
          iconPosition="right"
          tooltip={col.Header}
          labelColor={secondColor}
          onClick={(e) => order(e)}
          label={col.NormalizedName}
          icon={
            <>
              {columnOrderHeaderIcon(iconForm, col)}
              {columnInfoHeaderIcon(infoElement, col)}
            </>
          }
        />
      );
    }

    return undefined;
  };

  /**
   * Builds the visible DataTable columns from the selected-column metadata.
   */
  const dynamicColumns = selectedColumns.map((col) => {
    let iconForm = "default";

    if (orderInfo.columnName === col.Header) {
      if (orderInfo.orderbool === false) {
        iconForm = "asc";
      }

      if (orderInfo.orderbool === true) {
        iconForm = "desc";
      }
    }

    if (removeColumns?.includes(col.Name)) {
      return null;
    }

    if (col.Type === "boolean" || col.Type === "bool") {
      return (
        <Column
          id="head"
          key={col?.Name}
          dataType={col?.Type}
          field={col?.Name}
          header={headerElement(col, iconForm)}
          body={(data) => verifiedBodyTemplateLocal(data, col.Name)}
          headerStyle={{
            borderBottom: `2px solid ${color}`,
            color: secondColor,
          }}
        />
      );
    }

    if (colorOptions?.get(col.Name) || col.Name === "Status") {
      return (
        <Column
          id="head"
          key={col?.Name}
          dataType={col.Type}
          field={col.Name}
          header={headerElement(col, iconForm)}
          showAddButton={true}
          body={(data) => statusBodyTemplateLocal(data, col.Name)}
          headerStyle={{
            borderBottom: `2px solid ${color}`,
            color: secondColor,
          }}
        />
      );
    }

    if (col.Type === "byte[]") {
      return (
        <Column
          id="head"
          key={col?.Name}
          dataType={col.Type}
          field={col.Name}
          header={headerElement(col, iconForm)}
          showAddButton={true}
          body={(data) => imageBodyTemplateLocal(data, col.Name)}
          headerStyle={{
            borderBottom: `2px solid ${color}`,
            color: secondColor,
          }}
        />
      );
    }

    if (col.Type === "datetime") {
      return (
        <Column
          id="head"
          key={col?.Name}
          dataType={col.Type}
          field={col.Name}
          header={headerElement(col, iconForm)}
          showAddButton={true}
          body={(data) => dateTimeBodyTemplateLocal(data, col.Name)}
          headerStyle={{
            borderBottom: `2px solid ${color}`,
            color: secondColor,
          }}
        />
      );
    }

    return (
      <Column
        id="head"
        key={col?.Name}
        dataType={col.Type}
        field={col.Name}
        header={headerElement(col, iconForm)}
        showAddButton={true}
        headerStyle={{
          borderBottom: `2px solid ${color}`,
          color: secondColor,
        }}
      />
    );
  });

  //#endregion

  //#region Render helpers

  /**
   * Renders the paginator with a layout adapted to the current screen width.
   *
   * @returns {JSX.Element} Paginator element.
   */
  function paginatorElement() {
    const commonProps = {
      className: "paginator",
      rows: pageSize,
      totalRecords: totalRows,
      first: firstRows,
      onPageChange: (e) => onPage(e),
      rowsPerPageOptions: [10, 20, 50, 100],
    };

    if (adjustingTableMedia3) {
      return (
        <Paginator
          {...commonProps}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        />
      );
    }

    return (
      <Paginator
        {...commonProps}
        template={{
          layout:
            "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown",
        }}
      />
    );
  }

  /**
   * Renders the main DataTable together with the paginator and external header.
   *
   * @returns {JSX.Element} Table layout.
   */
  function returnTable() {
    return (
      <div
        className="secondDivDataTable"
        style={{ overflowX: "auto" }}
        onContextMenu={(e) => {
          rightClick(e);
        }}
        role="menu"
        tabIndex={0}
      >
        <DataTable
          emptyMessage={t("LabelNoresultsFound")}
          className="defaultTable"
          tableStyle={{ minWidth: tableMinWidth }}
          scrollable
          header={
            <TableHeader
              views={views}
              selectedView={selectedView}
              showFilters={showFilters}
              metaType={metaType}
              setMetaPair={setMetaPair}
              metaPair={metaPair}
              allColumns={allColumns}
              color={color}
              removeColumns={removeColumns}
              removeFilters={removeFilters}
              filtersOnTable={filtersOnTable}
              changeView={changeView}
              onColumnToggle={onColumnToggle}
              selectedColumns={selectedColumns}
              filteredColumnModal={filteredColumnModal}
              valueFilteredColumnModal={valueFilteredColumnModal}
              globalFilterValue={globalFilterValue}
              globalSearch={globalSearch}
              inputFilter={inputFilter}
              value={value}
              selectedRows={selectedRows}
              newButtons={newButtons}
              entity={entity}
              includeTemplateExcel={includeTemplateExcel}
              permissions={permissions}
              includeImportExcel={includeImportExcel}
              removeItem={removeItem}
              isCrud={isCrud}
              setLoading={setLoading}
              replaceFunctions={replaceFunctions}
              setAdvFilterString={setAdvFilterString}
              columns={columns}
              setIsTable={setIsTable}
              setRow={setRow}
              setOperation={setOperation}
              handleOpenDialogAdd={handleOpenDialogAdd}
              handleOpenDelete={handleOpenDelete}
              handleOpenCopy={handleOpenCopy}
              handleOpenEdit={handleOpenEdit}
              setFilteredColumnModal={setFilteredColumnModal}
              setValueFilteredColumnModal={setValueFilteredColumnModal}
              getWithFilters={getWithFilters}
              setSelectedRows={setSelectedRows}
              setSelectedView={setSelectedView}
              get={get}
              setViews={setViews}
              setExpandedRows={setExpandedRows}
              groupField={groupField}
              setIsLoaded={setIsLoaded}
              setRows={setRows}
              setTotalRows={setTotalRows}
              valueIdField={valueIdField}
              bufferService={bufferService}
              FILTER={FILTER}
              orderInfo={orderInfo}
              pageSize={pageSize}
              page={page}
            />
          }
          value={rows}
          size="small"
          selection={!noSelect && selectedRows}
          selectionMode={!noSelect && "multiple"}
          stripedRows
          responsiveLayout="scroll"
          breakpoint="960px"
          scrollHeight={height}
          reorderableColumns
          sortOrder={sortFields("sortOrder", orderFields, groupField)}
          sortField={sortFields("sortField", orderFields, groupField)}
          groupRowsBy={groupField}
          rowGroupMode="subheader"
          expandableRowGroups={rowGroup || false}
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            if (e.data && expandedRows) {
              let newValue = e?.data.filter((x) => !expandedRows.includes(x));
              let oldValue = expandedRows.filter((x) => !e?.data.includes(x));

              if (oldValue.length > newValue.length) {
                setExpandedRows(
                  expandedRows.filter(
                    (prev) => prev[groupField] !== oldValue[0][groupField]
                  )
                );
              }

              if (newValue.length > oldValue.length) {
                setExpandedRows(e.data);
              }
            } else {
              setExpandedRows(e.data);
            }
          }}
          rowGroupHeaderTemplate={headerTemplate}
          rowGroupFooterTemplate={footerTemplate}
          ref={dt}
          onSelectionChange={onSelectionChange}
          contextMenuSelection={selectContext}
          onContextMenuSelectionChange={(e) => {
            setSelectContext(e.value);
            if (onPassContext) onPassContext(e.value);
          }}
          onContextMenu={(e) =>
            rebuildContextMenu().length !== 0 &&
            cm.current.show(e.originalEvent)
          }
        >
          {isSelectSingle ? (
            <Column
              headerStyle={{
                borderBottom: `2px solid ${color}`,
                color: secondColor,
              }}
              selectionMode="single"
              className="OpenTableCheckbox"
              frozen
            />
          ) : (
            !noSelect && (
              <Column
                headerStyle={{
                  borderBottom: `2px solid ${color}`,
                  color: secondColor,
                }}
                selectionMode="multiple"
                className="TableSelectionColumn"
                frozen
              />
            )
          )}
          {dynamicColumns}
        </DataTable>
        {paginatorElement()}
      </div>
    );
  }

  /**
   * Renders the CRUD body used by popup mode or full-page mode.
   *
   * @returns {JSX.Element} CRUD body.
   */
  function returnCrudMenu() {
    return (
      <CrudTable
        metaType={metaType}
        entity={entity}
        onGetWithFilters={getWithFilters}
        operation={operation}
        setIsTable={setIsTable}
        rowEdit={rowEdit}
        setRowEdit={setRowEdit}
        valueAdd={valueAdd}
        setValueAdd={setValueAdd}
        editMap={editMap}
        setEditMap={setEditMap}
        row={row}
        isChildTable={isChildTable}
        parentLine={parentLine}
        field={field}
        companies={companies}
        isCrudInPopUp={crudInPopUp(metaType)}
      />
    );
  }

  //#endregion

  //#region Render

  return (
    <>
      {isLoaded ? (
        <div style={loadingStylesFunc(loading, adjustingTableMedia2)}>
          <LoadingBar />
        </div>
      ) : (
        <div>
          {loading && (
            <div style={loadingStylesFunc(loading, adjustingTableMedia2)}>
              <LoadingBar />
            </div>
          )}

          {disable && (
            <div className="disable" style={{ ...loadingStyle }}></div>
          )}

          <ContextMenu
            model={columnContextMenu}
            onShow={(e) => cm.current.hide(e.originalEvent)}
            ref={ccm}
          />

          {rebuildContextMenu().length !== 0 && (
            <ContextMenu
              model={rebuildContextMenu()}
              onShow={(e) => ccm.current.hide(e.originalEvent)}
              onHide={(e) => {
                if (e && e.target.className !== "p-menuitem-link") {
                  if (onPassContext) onPassContext(null);
                }
                setSelectContext(null);
              }}
              ref={cm}
            />
          )}

          {crudInPopUp(metaType) && (
            <>
              {returnTable()}
              <MMessageBoard
                open={!isTable}
                onClose={() => setIsTable(false)}
                title={operation + " " + entity}
                minWidth={(match2 && "400px") || (match && "850px") || "900px"}
                body={returnCrudMenu()}
              />
            </>
          )}

          {!crudInPopUp(metaType) &&
            (isTable ? returnTable() : returnCrudMenu())}

          <TableMessageBoards
            openConfirmDelete={openConfirmDelete}
            handleCloseConfirmDelete={handleCloseConfirmDelete}
            entityName={entityName}
            postDelete={postDelete}
            openAudit={openAudit}
            closeAudit={closeAudit}
            sortAuditData={sortAuditData}
            auditActionColor={auditActionColor}
            auditData={auditData}
          />
        </div>
      )}
    </>
  );

  //#endregion
}

//#endregion

//#region PropTypes

MTable.propTypes = {
  entityName: PropTypes.string,
  useSessionStorage: PropTypes.bool,
  crud: PropTypes.bool,
  onPassLine: PropTypes.func,
  onPassContext: PropTypes.func,
  tableHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  manualId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rolePermissions: PropTypes.array,
  lineContext: PropTypes.array,
  extraButtons: PropTypes.array,
  removeContextItems: PropTypes.array,
  onHideLabels: PropTypes.func,
  toUseFilter: PropTypes.object,
  toUseFilterName: PropTypes.string,
  selectSingle: PropTypes.bool,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
  isParentTable: PropTypes.bool,
  isChildTable: PropTypes.bool,
  setParentLine: PropTypes.func,
  parentLine: PropTypes.array,
  field: PropTypes.string,
  roleName: PropTypes.string,
  FILTER: PropTypes.string,
  replaceFunctions: PropTypes.object,
  color: PropTypes.string,
  secondColor: PropTypes.string,
  rowGroup: PropTypes.bool,
  groupField: PropTypes.array,
  headerTemplate: PropTypes.element,
  footerTemplate: PropTypes.element,
  colorOptions: PropTypes.object,
  noSelect: PropTypes.bool,
  maxRows: PropTypes.number,
  includeImportExcel: PropTypes.bool,
  includeTemplateExcel: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  valueIdField: PropTypes.string,
  removeHeaders: PropTypes.array,
  filtersOnTable: PropTypes.bool,
  infoElement: PropTypes.object,
  orderFields: PropTypes.string,
  hideDefaultColor: PropTypes.bool,
  inputFilter: PropTypes.bool,
  stopSaveRows: PropTypes.bool,
  removeColumns: PropTypes.array,
  removeFilters: PropTypes.array,
  disable: PropTypes.bool,
  tabsRailWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDataLoaded: PropTypes.func,
  autoSelectFirstRow: PropTypes.bool,
};

//#endregion